import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveBar } from "@nivo/bar";
import { useAuth } from "../../contexts/AuthContext";
import { sessionService } from "../../services/sessionService";
import { activityService } from "../../services/activityService";
import { GlobalActivityService } from "../../services/globalActivityService";
import {
  generateDateID,
  getDayPost,
  getPerformanceLogsForDateRange,
} from "../../services/dayPostService";
import {
  Session,
  ActivityMeasurement,
  PerformanceLog,
} from "../../types/post";
import { ActivityType } from "../../types/activity";
import { formatTimeVariableValue } from "../../utils/timeDurationUtils";
import Header from "../../components/layout/Header";
import BottomNav from "../../components/layout/BottomNav";
import {
  ChevronLeftIcon,
  ChevronUpDownIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import ComingSoon from "../../components/ComingSoon";
import { useSubscription } from "../../providers/SubscriptionProvider";

const PERIOD_OPTIONS = [
  { label: "7 days", value: 7 },
  { label: "14 days", value: 14 },
  { label: "30 days", value: 30 },
  { label: "60 days", value: 60 },
  { label: "90 days", value: 90 },
];

const PEAK_SCORE_MAX = 130;
const PEAK_LINE_COLOR = "#3b82f6";

interface DayDataPoint {
  date: string;
  dateLabel: string;
  values: number[];
  peakScore?: number;
  [key: string]: string | number | number[] | undefined;
}

const peakAxisTicks = [0, 20, 40, 60, 80, 100, 130];

const toLocalISODate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const buildSmoothLinePath = (points: { x: number; y: number }[]): string => {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  let path = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const current = points[i];
    const cpX = (prev.x + current.x) / 2;
    path += ` C ${cpX} ${prev.y}, ${cpX} ${current.y}, ${current.x} ${current.y}`;
  }
  return path;
};

const renderPeakAxisLayer = (enabled: boolean) => (layerProps: any) => {
  if (!enabled) return null;
  const { innerWidth, innerHeight } = layerProps;

  return (
    <g>
      <line
        x1={innerWidth}
        x2={innerWidth}
        y1={0}
        y2={innerHeight}
        stroke="rgba(100,116,139,0.45)"
        strokeWidth={1}
      />
      {peakAxisTicks.map((tick) => {
        const y = innerHeight - (tick / PEAK_SCORE_MAX) * innerHeight;
        return (
          <g key={`peak-tick-${tick}`}>
            <line
              x1={innerWidth}
              x2={innerWidth + 5}
              y1={y}
              y2={y}
              stroke="rgba(100,116,139,0.45)"
              strokeWidth={1}
            />
            <text
              x={innerWidth + 8}
              y={y + 4}
              textAnchor="start"
              fontSize={11}
              fill="rgba(100,116,139,0.9)"
            >
              {tick}
            </text>
          </g>
        );
      })}
      <text
        transform={`translate(${innerWidth + 38}, ${innerHeight / 2}) rotate(90)`}
        textAnchor="middle"
        fontSize={12}
        fill="rgba(100,116,139,0.9)"
      >
        Peak Score
      </text>
    </g>
  );
};

const renderPeakLineLayer =
  (enabled: boolean, chartData: DayDataPoint[]) => (layerProps: any) => {
    if (!enabled) return null;
    const { xScale, innerHeight } = layerProps;
    if (typeof xScale !== "function") return null;

    const bandwidth =
      typeof xScale.bandwidth === "function" ? xScale.bandwidth() : 0;
    const points = chartData
      .filter(
        (d) => typeof d.peakScore === "number" && Number.isFinite(d.peakScore),
      )
      .map((d) => {
        const xValue = xScale(d.dateLabel);
        if (typeof xValue !== "number") return null;
        const clamped = Math.max(
          0,
          Math.min(PEAK_SCORE_MAX, d.peakScore as number),
        );
        return {
          x: xValue + bandwidth / 2,
          y: innerHeight - (clamped / PEAK_SCORE_MAX) * innerHeight,
        };
      })
      .filter((point): point is { x: number; y: number } => point !== null);

    if (points.length === 0) return null;
    const path = buildSmoothLinePath(points);

    return (
      <path
        d={path}
        fill="none"
        stroke={PEAK_LINE_COLOR}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    );
  };

interface SearchableActivityPickerProps {
  options: { id: string; name: string }[];
  selectedId: string;
  onSelect: (id: string) => void;
}

const SearchableActivityPicker: React.FC<SearchableActivityPickerProps> = ({
  options,
  selectedId,
  onSelect,
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((o) => o.id === selectedId);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const lower = query.toLowerCase();
    return options.filter((o) => o.name.toLowerCase().includes(lower));
  }, [options, query]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (id: string) => {
    onSelect(id);
    setIsOpen(false);
    setQuery("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect("");
    setQuery("");
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        className="flex items-center border border-base-300 rounded-md bg-base-100 cursor-pointer"
        onClick={() => {
          setIsOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        {isOpen ? (
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search activities..."
            className="appearance-none block w-full pl-4 pr-12 py-4 font-medium font-grotesk text-sm text-base-content/80 bg-transparent focus:outline-none focus:ring-0 focus:border-base-300 leading-5"
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setIsOpen(false);
                setQuery("");
              }
              if (e.key === "Enter" && filtered.length === 1) {
                handleSelect(filtered[0].id);
              }
            }}
          />
        ) : (
          <span
            className={`flex-1 pl-4 pr-12 py-4 font-grotesk text-sm font-medium truncate leading-5 ${selectedOption ? "text-base-content/80" : "text-base-content/40"}`}
          >
            {selectedOption?.name || "Select an activity..."}
          </span>
        )}
        <div className="flex items-center gap-1 pr-3 -ml-12">
          {selectedId && !isOpen && (
            <button
              onClick={handleClear}
              className="p-1 rounded hover:bg-base-100 transition-colors"
            >
              <XMarkIcon className="h-4 w-4 text-base-content/40" />
            </button>
          )}
          <ChevronUpDownIcon className="h-5 w-5 text-base-content/40 flex-shrink-0" />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-base-100 border border-base-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-base-content/50 font-grotesk">
              No activities found
            </div>
          ) : (
            filtered.map((opt) => (
              <button
                key={opt.id}
                onClick={() => handleSelect(opt.id)}
                className={`w-full text-left px-4 py-2.5 text-sm font-grotesk hover:bg-base-100 transition-colors ${
                  opt.id === selectedId
                    ? "bg-neutral/10 text-neutral font-medium"
                    : "text-base-content"
                }`}
              >
                {opt.name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const ActivityExerciseInsights: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();

  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [globalActivityTypes, setGlobalActivityTypes] = useState<
    Record<string, ActivityType[]>
  >({});

  const { isPlusOrHigher } = useSubscription();
  const [allActivityOptions, setAllActivityOptions] = useState<
    {
      id: string;
      name: string;
      variables: { name: string; defaultUnit?: string }[];
      globalCategoryID?: string;
    }[]
  >([]);

  const [selectedActivityId, setSelectedActivityId] = useState<string>("");
  const [selectedVariable, setSelectedVariable] = useState<string>("");
  const [variableFilters, setVariableFilters] = useState<
    Record<string, string>
  >({});
  const [periodDays, setPeriodDays] = useState<number>(30);
  const [sessions, setSessions] = useState<(Session & { id: string })[]>([]);
  const [showPeakScore, setShowPeakScore] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [peakScoresByDate, setPeakScoresByDate] = useState<
    Record<string, number>
  >({});

  const [allSessions, setAllSessions] = useState<(Session & { id: string })[]>(
    [],
  );
  const [performanceLogs, setPerformanceLogs] = useState<
    (PerformanceLog & { id: string })[]
  >([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingPeakScores, setLoadingPeakScores] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load user & global activity types
  useEffect(() => {
    if (!currentUser?.uid) return;

    const loadActivities = async () => {
      try {
        setLoadingActivities(true);
        const [userTypes, categories] = await Promise.all([
          activityService.fetchActivityTypes(currentUser.uid),
          GlobalActivityService.fetchCategories(),
        ]);

        setActivityTypes(userTypes);

        const allowedAdminCategoryIds = new Set(
          (userData?.adminActivityCatIDs || []).filter(
            (id): id is string => !!id,
          ),
        );

        const categoryIds = categories
          .map((c) => c.id)
          .filter(
            (id): id is string => !!id && allowedAdminCategoryIds.has(id),
          );

        let globalGrouped: Record<string, ActivityType[]> = {};
        if (categoryIds.length > 0) {
          const allGlobal =
            await GlobalActivityService.fetchActivityTypesForCategories(
              categoryIds,
            );
          globalGrouped = allGlobal.reduce<Record<string, ActivityType[]>>(
            (acc, type) => {
              const catId = type.categoryId;
              if (!catId) return acc;
              if (!acc[catId]) acc[catId] = [];
              acc[catId].push({
                id: type.id,
                name: type.name,
                description: type.description,
                variables: type.variables,
                image: type.image,
                isFromPack: true,
                categoryId: type.categoryId,
              });
              return acc;
            },
            {},
          );
        }
        setGlobalActivityTypes(globalGrouped);

        // Build combined, deduplicated options list
        const optionsMap = new Map<string, (typeof allActivityOptions)[0]>();

        for (const t of userTypes) {
          if (t.categoryId && !allowedAdminCategoryIds.has(t.categoryId)) {
            continue;
          }

          if (t.id) {
            optionsMap.set(t.id, {
              id: t.id,
              name: t.name,
              globalCategoryID: t.categoryId,
              variables: (t.variables || []).map((v: any) => ({
                name: typeof v === "string" ? v : v.name,
                defaultUnit: typeof v === "string" ? undefined : v.defaultUnit,
              })),
            });
          }
        }

        for (const [catId, types] of Object.entries(globalGrouped)) {
          for (const t of types) {
            if (t.id && !optionsMap.has(t.id)) {
              optionsMap.set(t.id, {
                id: t.id,
                name: t.name,
                globalCategoryID: catId,
                variables: (t.variables || []).map((v: any) => ({
                  name: typeof v === "string" ? v : v.name,
                  defaultUnit:
                    typeof v === "string" ? undefined : v.defaultUnit,
                })),
              });
            }
          }
        }

        const opts = Array.from(optionsMap.values()).sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        setAllActivityOptions(opts);
      } catch (err) {
        console.error("Error loading activity types:", err);
        setError("Failed to load activities");
      } finally {
        setLoadingActivities(false);
      }
    };

    loadActivities();
  }, [currentUser?.uid, userData?.adminActivityCatIDs]);

  // Fetch all sessions upfront to determine which activities have data
  useEffect(() => {
    if (!currentUser?.uid) return;

    const fetchAllSessions = async () => {
      try {
        setLoadingSessions(true);
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - periodDays + 1);

        const data = await sessionService.getSessionsForDateRange(
          currentUser.uid,
          toLocalISODate(startDate),
          toLocalISODate(endDate),
        );
        setAllSessions(data);
      } catch (err) {
        console.error("Error fetching all sessions:", err);
      } finally {
        setLoadingSessions(false);
      }
    };

    fetchAllSessions();
  }, [currentUser?.uid, periodDays]);

  // Fetch performance logs for the same period (performances no longer create a
  // session, so they are plotted from their denormalized `activity` field)
  useEffect(() => {
    if (!currentUser?.uid) return;

    const fetchPerformanceLogs = async () => {
      try {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - periodDays + 1);

        const logs = await getPerformanceLogsForDateRange(
          currentUser.uid,
          generateDateID(startDate),
          generateDateID(endDate),
        );
        setPerformanceLogs(logs.filter((l) => l.activity));
      } catch (err) {
        console.error("Error fetching performance logs:", err);
      }
    };

    fetchPerformanceLogs();
  }, [currentUser?.uid, periodDays]);

  // Compute which activity IDs have data in the fetched sessions / performances
  const activityIdsWithData = useMemo(() => {
    const ids = new Set<string>();
    const addRawId = (id: string) => {
      const rawId = id.startsWith("global_")
        ? id.split("_").length >= 3
          ? id.split("_").slice(2).join("_")
          : id
        : id;
      ids.add(rawId);
    };
    for (const session of allSessions) {
      for (const m of session.ActivityMeasurementList || []) {
        addRawId(m.ActivityTypeID);
      }
    }
    for (const log of performanceLogs) {
      if (log.activity?.activityTypeID) addRawId(log.activity.activityTypeID);
    }
    return ids;
  }, [allSessions, performanceLogs]);

  // Filter activity options to only those with session / performance data
  const filteredActivityOptions = useMemo(() => {
    if (
      allSessions.length === 0 &&
      performanceLogs.length === 0 &&
      !loadingSessions
    )
      return [];
    if (activityIdsWithData.size === 0) return [];
    return allActivityOptions.filter((opt) => activityIdsWithData.has(opt.id));
  }, [
    allActivityOptions,
    activityIdsWithData,
    allSessions,
    performanceLogs,
    loadingSessions,
  ]);

  // Derive available variables when activity changes
  const selectedActivityOption = useMemo(
    () => allActivityOptions.find((o) => o.id === selectedActivityId),
    [allActivityOptions, selectedActivityId],
  );

  const variableOptions = useMemo(() => {
    if (!selectedActivityOption) return [];
    return selectedActivityOption.variables;
  }, [selectedActivityOption]);

  // Reset variable + filters when activity changes
  useEffect(() => {
    if (variableOptions.length > 0) {
      setSelectedVariable(variableOptions[0].name);
    } else {
      setSelectedVariable("");
    }
    setVariableFilters({});
  }, [selectedActivityId, variableOptions]);

  // Reset filters when primary variable changes
  useEffect(() => {
    setVariableFilters({});
  }, [selectedVariable]);

  // Reuse allSessions for chart data (already fetched for the same date range)
  useEffect(() => {
    if (!selectedActivityId || !selectedVariable) {
      setSessions([]);
      return;
    }
    setSessions(allSessions);
  }, [allSessions, selectedActivityId, selectedVariable]);

  // Fetch day-level peak scores only when toggle is enabled
  useEffect(() => {
    if (!showPeakScore) {
      setPeakScoresByDate({});
      setLoadingPeakScores(false);
      return;
    }

    if (!currentUser?.uid || !selectedActivityId || !selectedVariable) {
      setPeakScoresByDate({});
      return;
    }

    let cancelled = false;

    const fetchPeakScores = async () => {
      try {
        setLoadingPeakScores(true);

        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - periodDays + 1);

        const dayDates: Date[] = [];
        const cursor = new Date(startDate);
        while (cursor <= endDate) {
          dayDates.push(new Date(cursor));
          cursor.setDate(cursor.getDate() + 1);
        }

        const dayPosts = await Promise.all(
          dayDates.map((date) =>
            getDayPost(currentUser.uid, generateDateID(date)),
          ),
        );

        const nextMap: Record<string, number> = {};
        dayPosts.forEach((dayPost, idx) => {
          const score = dayPost?.peakScoreLog?.peakScore;
          if (typeof score === "number" && Number.isFinite(score)) {
            nextMap[toLocalISODate(dayDates[idx])] = score;
          }
        });

        if (!cancelled) {
          setPeakScoresByDate(nextMap);
        }
      } catch (err) {
        console.error("Error fetching peak score data:", err);
      } finally {
        if (!cancelled) {
          setLoadingPeakScores(false);
        }
      }
    };

    fetchPeakScores();
    return () => {
      cancelled = true;
    };
  }, [
    showPeakScore,
    currentUser?.uid,
    selectedActivityId,
    selectedVariable,
    periodDays,
  ]);

  const getRawId = (id: string) => {
    if (id.startsWith("global_")) {
      const parts = id.split("_");
      return parts.length >= 3 ? parts.slice(2).join("_") : id;
    }
    return id;
  };

  // Collect all matching measurements (with their date) for the selected
  // activity, from both sessions and performance logs.
  const matchingMeasurements = useMemo(() => {
    if (!selectedActivityId) return [];

    const results: {
      date: string;
      variables: ActivityMeasurement["variables"];
    }[] = [];
    for (const session of sessions) {
      for (const m of session.ActivityMeasurementList || []) {
        if (getRawId(m.ActivityTypeID) !== selectedActivityId) continue;
        results.push({ date: session.plannedDate, variables: m.variables });
      }
    }
    for (const log of performanceLogs) {
      const act = log.activity;
      if (!act) continue;
      if (getRawId(act.activityTypeID) !== selectedActivityId) continue;
      const rawDate = log.performanceDate || log.dateID;
      if (!rawDate || rawDate.length < 8) continue;
      const isoDate = `${rawDate.substring(0, 4)}-${rawDate.substring(
        4,
        6,
      )}-${rawDate.substring(6, 8)}`;
      results.push({ date: isoDate, variables: act.variables });
    }
    return results;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, performanceLogs, selectedActivityId]);

  // For each non-primary variable, compute sorted distinct values from the data
  const filterVariableOptions = useMemo(() => {
    if (!selectedVariable || matchingMeasurements.length === 0) return [];

    const otherVarNames = variableOptions
      .map((v) => v.name)
      .filter((name) => name !== selectedVariable);

    return otherVarNames
      .map((varName) => {
        const valueSet = new Set<number>();
        for (const mm of matchingMeasurements) {
          const v = mm.variables.find((x) => x.name === varName);
          if (v?.measurement != null) valueSet.add(v.measurement);
        }
        const distinctValues = Array.from(valueSet).sort((a, b) => a - b);
        const unitHint =
          variableOptions.find((vo) => vo.name === varName)?.defaultUnit || "";
        return { name: varName, values: distinctValues, unit: unitHint };
      })
      .filter((fv) => fv.values.length > 0);
  }, [selectedVariable, matchingMeasurements, variableOptions]);

  // Build chart data from matching measurements, applying variable filters
  const { chartData, maxKeys, unit } = useMemo(() => {
    if (!selectedActivityId || !selectedVariable) {
      return { chartData: [] as DayDataPoint[], maxKeys: 0, unit: "" };
    }

    const dateMap = new Map<string, number[]>();
    let detectedUnit = "";

    for (const mm of matchingMeasurements) {
      const primaryVar = mm.variables.find((v) => v.name === selectedVariable);
      if (!primaryVar || primaryVar.measurement == null) continue;

      // Check all active filters
      let passesFilters = true;
      for (const [filterVarName, filterValue] of Object.entries(
        variableFilters,
      )) {
        if (!filterValue) continue; // "All" — no filtering
        const siblingVar = mm.variables.find((v) => v.name === filterVarName);
        if (siblingVar?.measurement == null) {
          passesFilters = false;
          break;
        }
        if (String(siblingVar.measurement) !== filterValue) {
          passesFilters = false;
          break;
        }
      }
      if (!passesFilters) continue;

      if (!detectedUnit && primaryVar.unit) detectedUnit = primaryVar.unit;

      if (!dateMap.has(mm.date)) dateMap.set(mm.date, []);
      dateMap.get(mm.date)!.push(primaryVar.measurement);
    }

    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - periodDays + 1);

    const result: DayDataPoint[] = [];
    let maxKeyCount = 0;
    const current = new Date(startDate);

    while (current <= endDate) {
      const dateStr = toLocalISODate(current);
      const dayValues = dateMap.get(dateStr) || [];

      dayValues.sort((a, b) => a - b);
      maxKeyCount = Math.max(maxKeyCount, dayValues.length);

      const label =
        periodDays <= 14
          ? current.toLocaleDateString("en-US", {
              weekday: "short",
              day: "numeric",
              month: "short",
            })
          : current.toLocaleDateString("en-US", {
              day: "numeric",
              month: "short",
            });

      const point: DayDataPoint = {
        date: dateStr,
        dateLabel: label,
        values: dayValues,
        peakScore: showPeakScore ? peakScoresByDate[dateStr] : undefined,
      };

      const sorted = [...dayValues].sort((a, b) => b - a);
      sorted.forEach((val, idx) => {
        point[`set_${idx}`] = val;
      });

      result.push(point);
      current.setDate(current.getDate() + 1);
    }

    return { chartData: result, maxKeys: maxKeyCount, unit: detectedUnit };
  }, [
    matchingMeasurements,
    selectedActivityId,
    selectedVariable,
    variableFilters,
    periodDays,
    showPeakScore,
    peakScoresByDate,
  ]);

  const barKeys = useMemo(
    () => Array.from({ length: maxKeys }, (_, i) => `set_${i}`),
    [maxKeys],
  );

  const yMax = useMemo(() => {
    const allValues = chartData.flatMap((d) => d.values);
    if (allValues.length === 0) return 10;
    const max = Math.max(...allValues);
    // Add 10% headroom above the tallest bar
    return max + max * 0.1;
  }, [chartData]);

  const hasData = chartData.some((d) => d.values.length > 0);
  const hasPeakData = chartData.some(
    (d) => typeof d.peakScore === "number" && Number.isFinite(d.peakScore),
  );

  return (
    <div className="w-full pt-16 pb-24 bg-base-200 min-h-screen">
      <Header />

      {/* Header */}
      <div className="shadow-sm">
        <div className="mx-auto max-w-4xl px-4 py-4">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-full hover:bg-base-100 transition-colors bg-base-300"
              >
                <ChevronLeftIcon className="h-5 w-5 text-base-content/80" />
              </button>
              <h1 className="text-2xl font-grotesk font-bold text-base-content">
                Activities & Exercises
              </h1>
            </div>
          </div>
        </div>
      </div>

      {!isPlusOrHigher && (
        <div className="max-w-4xl mx-auto py-8 px-4">
          <ComingSoon featureKey="activityInsights" />
        </div>
      )}
      {isPlusOrHigher && (
        <div className="max-w-4xl mx-auto py-0 md:py-4 px-0 md:px-4">
          <div className="max-w-4xl mx-auto py-0 md:py-4 px-0 md:px-4">
            {/* Filters */}
            <div className=" p-6 space-y-4">
              {/* Time period */}
              <div className="space-y-1">
                <label className="block text-xs font-medium text-base-content/50 uppercase tracking-wide font-grotesk">
                  Time Period
                </label>
                <select
                  value={periodDays}
                  onChange={(e) => setPeriodDays(Number(e.target.value))}
                  className="appearance-none block w-full pl-4 pr-12 py-4 border font-medium border-base-300 rounded-md leading-5 bg-base-100 text-base-content/80 focus:outline-none focus:ring-0 focus:border-base-300 font-grotesk text-sm cursor-pointer"
                >
                  {PERIOD_OPTIONS.filter(
                    (opt) => isPlusOrHigher || opt.value <= 30,
                  ).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Activity picker (searchable) */}
              <div className="space-y-1">
                <label className="block text-xs font-medium text-base-content/50 uppercase tracking-wide font-grotesk">
                  Activity
                </label>
                {loadingActivities || loadingSessions ? (
                  <div className="h-14 bg-base-100 rounded-md animate-pulse" />
                ) : (
                  <SearchableActivityPicker
                    options={filteredActivityOptions}
                    selectedId={selectedActivityId}
                    onSelect={(id) => setSelectedActivityId(id)}
                  />
                )}
              </div>

              {/* Variable picker */}
              {selectedActivityId && variableOptions.length > 0 && (
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-base-content/50 uppercase tracking-wide font-grotesk">
                    Measurement
                  </label>
                  <select
                    value={selectedVariable}
                    onChange={(e) => setSelectedVariable(e.target.value)}
                    className="appearance-none block w-full pl-4 pr-12 py-4 border font-medium border-base-300 rounded-md leading-5 bg-base-100 text-base-content/80 focus:outline-none focus:ring-0 focus:border-base-300 font-grotesk text-sm cursor-pointer"
                  >
                    {variableOptions.map((v) => (
                      <option key={v.name} value={v.name}>
                        {v.name}
                        {v.defaultUnit ? ` (${v.defaultUnit})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Filter by button */}
              {filterVariableOptions.length > 0 && (
                <div className="pt-2">
                  <button
                    onClick={() => setShowFilterModal(true)}
                    className="flex items-center gap-2 text-sm font-geist font-medium text-base-content/60 hover:text-base-content transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Filter by
                    {Object.values(variableFilters).filter(Boolean).length >
                      0 && (
                      <span className="ml-1 bg-neutral text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {Object.values(variableFilters).filter(Boolean).length}
                      </span>
                    )}
                  </button>
                </div>
              )}

              {/* Filter modal */}
              {showFilterModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                  <div
                    className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                    onClick={() => setShowFilterModal(false)}
                  />
                  <div className="relative bg-base-100 rounded-2xl w-full max-w-md mx-4 p-6 space-y-4 max-h-[80vh] overflow-y-auto">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-heading font-semibold text-base-content">
                        Filter by
                      </h3>
                      <button
                        onClick={() => setShowFilterModal(false)}
                        className="p-1.5 rounded-full hover:bg-base-200 transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-base-content/60"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                    <div className="space-y-4">
                      {filterVariableOptions.map((fv) => (
                        <div key={fv.name} className="space-y-1">
                          <label className="block text-xs font-medium text-base-content/50 uppercase tracking-wide font-grotesk">
                            {fv.name}
                            {fv.unit ? ` (${fv.unit})` : ""}
                          </label>
                          <select
                            value={variableFilters[fv.name] || ""}
                            onChange={(e) =>
                              setVariableFilters((prev) => ({
                                ...prev,
                                [fv.name]: e.target.value,
                              }))
                            }
                            className="appearance-none block w-full pl-4 pr-12 py-3 border font-medium border-base-300 rounded-md leading-5 bg-base-100 text-base-content/80 focus:outline-none focus:ring-0 focus:border-base-300 font-grotesk text-sm cursor-pointer"
                          >
                            <option value="">All</option>
                            {fv.values.map((val) => (
                              <option key={val} value={String(val)}>
                                {val}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => setShowFilterModal(false)}
                      className="w-full mt-4 py-3 rounded-lg bg-neutral text-white font-grotesk font-medium text-sm hover:opacity-90 transition-opacity"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              )}

              {/* Chart area */}
              {selectedActivityId && selectedVariable ? (
                <div className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-heading font-semibold text-base-content">
                        {selectedActivityOption?.name} — {selectedVariable}
                      </h2>
                      <div className="flex items-center gap-2 flex-wrap">
                        {unit && (
                          <p className="text-xs font-grotesk text-base-content/50">
                            Unit: {unit}
                          </p>
                        )}
                        {Object.entries(variableFilters)
                          .filter(([, val]) => val)
                          .map(([name, val]) => (
                            <span
                              key={name}
                              className="text-xs font-grotesk bg-neutral/10 text-neutral px-2 py-0.5 rounded-full"
                            >
                              {name}: {val}
                            </span>
                          ))}
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm font-grotesk text-base-content/80 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={showPeakScore}
                        onChange={(e) => setShowPeakScore(e.target.checked)}
                        className="checkbox checkbox-sm"
                      />
                      <span>Peak Score</span>
                    </label>
                  </div>

                  {loadingSessions || (showPeakScore && loadingPeakScores) ? (
                    <div className="h-80 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral" />
                    </div>
                  ) : error ? (
                    <div className="h-80 flex items-center justify-center">
                      <p className="text-red-500 font-grotesk">{error}</p>
                    </div>
                  ) : !hasData && !(showPeakScore && hasPeakData) ? (
                    <div className="h-80 flex flex-col items-center justify-center text-base-content/50">
                      <p className="font-grotesk text-sm">
                        No data found for this activity in the selected period.
                      </p>
                      <p className="font-grotesk text-xs mt-1">
                        Complete sessions with this activity to see data here.
                      </p>
                    </div>
                  ) : (
                    <div
                      className={`w-full ${periodDays > 7 ? "overflow-x-auto md:overflow-x-visible" : ""}`}
                    >
                      <div
                        className="h-80"
                        style={
                          periodDays > 7
                            ? {
                                minWidth: `${Math.max(chartData.length * 35, 500)}px`,
                              }
                            : undefined
                        }
                      >
                        <ResponsiveBar
                          data={chartData as any}
                          keys={barKeys}
                          indexBy="dateLabel"
                          groupMode="grouped"
                          margin={{
                            top: 10,
                            right: showPeakScore ? 60 : 10,
                            bottom: periodDays <= 14 ? 60 : 80,
                            left: 50,
                          }}
                          padding={0.5}
                          innerPadding={1}
                          valueScale={
                            { type: "linear", min: 0, max: yMax } as any
                          }
                          indexScale={{ type: "band", round: true }}
                          colors={() => "#6b7280"}
                          borderRadius={3}
                          axisTop={null}
                          axisRight={null}
                          axisBottom={{
                            tickSize: 0,
                            tickPadding: 8,
                            tickRotation: periodDays <= 14 ? -30 : -45,
                            legendPosition: "middle" as const,
                            legendOffset: periodDays <= 14 ? 50 : 70,
                            ...(periodDays > 30
                              ? {
                                  renderTick: (tick: any) => {
                                    const tickIndex = chartData.findIndex(
                                      (d) => d.dateLabel === tick.value,
                                    );
                                    const interval =
                                      periodDays <= 60
                                        ? 3
                                        : periodDays <= 90
                                          ? 5
                                          : 7;
                                    if (tickIndex % interval !== 0)
                                      return <g />;
                                    return (
                                      <g
                                        transform={`translate(${tick.x},${tick.y + 16})`}
                                      >
                                        <text
                                          textAnchor="end"
                                          dominantBaseline="central"
                                          transform="rotate(-45)"
                                          style={{
                                            fontSize: 11,
                                            fill: "currentColor",
                                          }}
                                        >
                                          {tick.value}
                                        </text>
                                      </g>
                                    );
                                  },
                                }
                              : {}),
                          }}
                          axisLeft={{
                            tickSize: 0,
                            tickPadding: 8,
                            legend: unit || selectedVariable,
                            legendPosition: "middle" as const,
                            legendOffset: -40,
                          }}
                          layers={[
                            "grid",
                            "axes",
                            "bars",
                            renderPeakLineLayer(showPeakScore, chartData),
                            "markers",
                            "legends",
                            "annotations",
                            renderPeakAxisLayer(showPeakScore),
                          ]}
                          enableLabel={false}
                          animate={true}
                          motionConfig="gentle"
                          tooltip={({ value, indexValue, color }) => (
                            <div className="bg-base-100 border border-base-300 rounded-lg shadow-lg px-4 py-2 min-w-[180px]">
                              <p className="text-xs font-grotesk text-base-content/60">
                                {indexValue}
                              </p>
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-2.5 h-2.5 rounded-sm"
                                  style={{ backgroundColor: color }}
                                />
                                <p className="text-sm font-grotesk font-medium text-base-content">
                                  {formatTimeVariableValue(
                                    selectedVariable,
                                    value,
                                    unit,
                                  ) ?? `${value} ${unit}`}
                                </p>
                              </div>
                              {showPeakScore &&
                                (() => {
                                  const dayMatch = chartData.find(
                                    (d) => d.dateLabel === indexValue,
                                  );
                                  const score = dayMatch?.peakScore;
                                  if (typeof score !== "number") return null;
                                  return (
                                    <div className="flex items-center gap-2 mt-1">
                                      <div
                                        className="w-2.5 h-2.5 rounded-full"
                                        style={{
                                          backgroundColor: PEAK_LINE_COLOR,
                                        }}
                                      />
                                      <p className="text-xs font-grotesk text-base-content/80">
                                        Peak Score: {Math.round(score)}
                                      </p>
                                    </div>
                                  );
                                })()}
                            </div>
                          )}
                        />
                      </div>
                    </div>
                  )}

                  {/* Summary stats */}
                  {hasData && !loadingSessions && (
                    <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-base-200">
                      {(() => {
                        const allValues = chartData.flatMap((d) => d.values);
                        const total = allValues.reduce((s, v) => s + v, 0);
                        const avg =
                          allValues.length > 0 ? total / allValues.length : 0;
                        const max =
                          allValues.length > 0 ? Math.max(...allValues) : 0;
                        const daysWithData = chartData.filter(
                          (d) => d.values.length > 0,
                        ).length;

                        return (
                          <>
                            <div className="text-center">
                              <p className="text-xs font-grotesk text-base-content/50">
                                Average
                              </p>
                              <p className="text-lg font-heading font-semibold text-base-content">
                                {formatTimeVariableValue(
                                  selectedVariable,
                                  avg,
                                  unit,
                                ) ?? (
                                  <>
                                    {avg.toFixed(1)}
                                    <span className="text-xs font-grotesk text-base-content/50 ml-1">
                                      {unit}
                                    </span>
                                  </>
                                )}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs font-grotesk text-base-content/50">
                                Max
                              </p>
                              <p className="text-lg font-heading font-semibold text-base-content">
                                {formatTimeVariableValue(
                                  selectedVariable,
                                  max,
                                  unit,
                                ) ?? (
                                  <>
                                    {max.toFixed(1)}
                                    <span className="text-xs font-grotesk text-base-content/50 ml-1">
                                      {unit}
                                    </span>
                                  </>
                                )}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs font-grotesk text-base-content/50">
                                Days Active
                              </p>
                              <p className="text-lg font-heading font-semibold text-base-content">
                                {daysWithData}
                                <span className="text-xs font-grotesk text-base-content/50 ml-1">
                                  / {periodDays}
                                </span>
                              </p>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ) : (
                <div className="pt-4 text-center">
                  <p className="text-base-content/50 font-grotesk">
                    Select an activity and measurement above to view your data.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <BottomNav />
    </div>
  );
};

export default ActivityExerciseInsights;
